import { logger } from 'v2/utils/logger'
import { createGlobalHeaders, getActiveRegion } from '../shared/services/create-service'
import { embedDpopHeaders } from '../shared/services/service-interceptors'
import {
  dispatchEmbedSessionExpired,
  getEmbedSession,
  isEmbedMode,
  toEmbedPath,
} from '../shared/utils/embed-session'

export interface StreamHandlers<TData> {
  onMessage: (data: TData) => void
  onError: (error: unknown) => void
  onEvent?: (event: string | undefined, data: TData) => void
  onDone?: () => void
}

export type StreamFunction = <TBody, TData>(
  path: string,
  body: TBody,
  handlers: StreamHandlers<TData>,
) => AbortController

/**
 * Creates an SSE stream connection with automatic parsing and error handling
 * @param baseURL - The base URL for the service
 * @param path - The endpoint path to stream from
 * @param body - The request body to send
 * @param handlers - Event handlers for messages, errors, and completion
 * @returns AbortController to control the stream
 */
export const createSSEStream = <TBody, TData>(
  baseURL: string,
  path: string,
  body: TBody,
  handlers: StreamHandlers<TData>,
): AbortController => {
  const controller = new AbortController()
  let serviceBaseURL = baseURL?.replace(/\/$/, '') || ''

  // Manually replace {{region}} placeholder since fetch bypasses axios interceptors
  if (serviceBaseURL.includes('{{region}}')) {
    const region = getActiveRegion()
    if (!region) {
      handlers.onError(new Error('Region is not set, cannot make request'))
      handlers.onDone?.()
      return controller
    }
    serviceBaseURL = serviceBaseURL.replace('{{region}}', region)
  }

  // fetch bypasses the axios interceptors, so embed rewrites the URL here too (identity elsewhere).
  const url = toEmbedPath(`${serviceBaseURL}${path}`)
  // The proof is async but the controller must be returned synchronously, so the fetch is issued
  // once the proof resolves ({} outside embed); `signal` still cancels an early abort.
  embedDpopHeaders('POST', url, isEmbedMode() ? getEmbedSession() : null)
    .then((proof) =>
      fetch(url, {
        method: 'POST',
        headers: {
          ...createGlobalHeaders(),
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...proof,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      }),
    )
    .then(async (res) => {
      if (!res.ok || !res.body) {
        // Mirror the axios embed 401 handling (expiry overlay + host re-mint).
        if (res.status === 401 && isEmbedMode()) {
          dispatchEmbedSessionExpired()
        }
        try {
          const text = await res.text()
          handlers.onError(new Error(`HTTP ${res.status}: ${text}`))
        } catch (error) {
          handlers.onError(error)
        }
        handlers.onDone?.()
        return
      }
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      try {
        while (true) {
          // Check if the request was aborted before reading
          if (controller.signal.aborted) {
            logger.debug('Stream aborted, breaking read loop')
            break
          }

          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const parts = buffer.split('\n\n')
          buffer = parts.pop() || ''
          parts.forEach((part) => {
            const lines = part.split('\n')
            let event = ''
            let dataLine = ''
            lines.forEach((line) => {
              if (line.startsWith('event:')) event = line.slice(6).trim()
              if (line.startsWith('data:')) dataLine = line.slice(5).trim()
            })
            if (dataLine) {
              try {
                const raw = JSON.parse(dataLine) as unknown
                const data = raw as TData

                if (handlers.onEvent) {
                  handlers.onEvent(event || undefined, data)
                }

                if (event === 'error') {
                  handlers.onError(raw)
                } else {
                  handlers.onMessage(data)
                }
              } catch (e) {
                logger.error('SSE parse error:', e, { dataLine })
                handlers.onError(e as Error)
              }
            }
          })
        }
      } finally {
        // Always close the reader to free up resources
        reader.releaseLock()
      }

      handlers.onDone?.()
    })
    .catch((err: unknown) => {
      if ((err as Error).name === 'AbortError') {
        logger.debug('Stream aborted')
        return
      }
      handlers.onError(err)
      handlers.onDone?.()
    })
  return controller
}
